import { useState, useEffect, type SyntheticEvent } from "react";
import axios from "../plugins/axios";
import { TextField, Autocomplete } from "@mui/material";

const apiUrl = process.env.REACT_APP_WORKNEST_TOOLS_API_URL;

export interface AccountOption {
  value: string | number;
  text: string;
}

interface Props {
  handleAccountChange: (v: AccountOption | null) => void;
  label?: string;
}

const AccountAutoComplete = ({ handleAccountChange, label = "" }: Props) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<AccountOption[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState<AccountOption | null>(null);

  useEffect(() => {
    let active = true;

    if (!input || input.length < 3) {
      setOptions([]);
      return undefined;
    }

    (async () => {
      setLoading(true);
      const clients = (await axios.get<AccountOption[]>(`${apiUrl}/getclients/` + encodeURIComponent(input))).data;
      if (active) {
        setOptions(clients);
      }
      setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [open, input]);

  const onChange = (_e: SyntheticEvent, v: AccountOption | string | null) => {
    const opt = v && typeof v !== "string" ? v : null;
    handleAccountChange(opt);
    setValue(opt);
  };

  const getOptionLabel = (option: AccountOption | string) =>
    typeof option === "string" ? option : option.text || "";

  return (
    <Autocomplete<AccountOption, false, false, true>
      freeSolo
      inputValue={input}
      open={open}
      value={value}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      getOptionLabel={getOptionLabel}
      // Two options can share the same `text` (e.g. duplicate Salesforce accounts);
      // use the unique `value` (Salesforce ID) as the React key.
      renderOption={(props, option) => {
        const { key: _key, ...rest } = props;
        return (
          <li key={option.value} {...rest}>
            {option.text}
          </li>
        );
      }}
      onChange={onChange}
      options={options}
      loading={loading}
      onInputChange={(_e, val) => setInput(val)}
      renderInput={(params) => (
        <TextField {...params} fullWidth size="small" name="noAutoFill" label={label} />
      )}
    />
  );
};

export default AccountAutoComplete;
